<script setup>
const props = defineProps({
  points: {
    type: Array,
    default: () => [],
  },
  usableHours: {
    type: Number,
    default: 0,
  },
})

const width = 640
const height = 220
const padding = 28

const maxY = computed(() => Math.max(props.usableHours, ...props.points.map(point => point.actual), 1))

function x(index) {
  if (props.points.length <= 1) return padding
  return padding + ((width - (padding * 2)) * (index / (props.points.length - 1)))
}

function y(value) {
  return height - padding - ((height - (padding * 2)) * (value / maxY.value))
}

function pathFor(key) {
  if (!props.points.length) return ''

  return props.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(index)} ${y(point[key])}`).join(' ')
}
</script>

<template>
  <VCard class="review-widget capacity-widget">
    <VCardText class="pa-5">
      <div class="d-flex align-center justify-space-between gap-4 mb-4">
        <div>
          <div class="text-overline text-success mb-1">{{ $t('dashboard.weeklyReview.capacityOverline') }}</div>
          <h2 class="text-h6 font-weight-bold mb-0">{{ $t('dashboard.weeklyReview.capacityGrowLine') }}</h2>
        </div>
        <VChip color="success" variant="tonal">{{ usableHours.toFixed(0) }}h ceiling</VChip>
      </div>

      <svg class="capacity-chart" :viewBox="`0 0 ${width} ${height}`" role="img">
        <line :x1="padding" :x2="width - padding" :y1="y(usableHours)" :y2="y(usableHours)" class="capacity-ceiling" />
        <path :d="pathFor('ideal')" class="capacity-line capacity-line--ideal" />
        <path :d="pathFor('actual')" class="capacity-line capacity-line--actual" />
        <g v-for="(point, index) in points" :key="point.label">
          <circle :cx="x(index)" :cy="y(point.actual)" r="4" class="capacity-dot" />
          <text :x="x(index)" :y="height - 6" text-anchor="middle" class="capacity-label">{{ point.label }}</text>
        </g>
      </svg>
    </VCardText>
  </VCard>
</template>

<style scoped lang="scss">
.review-widget {
  block-size: 100%;
  border: 1px solid rgba(var(--v-border-color), 0.12);
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(var(--v-theme-surface), 0.86), rgba(var(--v-theme-surface), 0.64)),
    rgba(8, 12, 20, 0.72);
  backdrop-filter: blur(14px) saturate(1.1);
}

.capacity-chart {
  inline-size: 100%;
  block-size: 240px;
}

.capacity-ceiling {
  stroke: rgba(148, 163, 184, 0.32);
  stroke-dasharray: 6 6;
  stroke-width: 1.5;
}

.capacity-line {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.capacity-line--ideal {
  stroke: rgba(148, 163, 184, 0.55);
  stroke-dasharray: 8 8;
  stroke-width: 3;
}

.capacity-line--actual {
  filter: drop-shadow(0 0 8px rgba(34, 197, 94, 0.5));
  stroke: #22c55e;
  stroke-width: 4;
}

.capacity-dot {
  fill: #22c55e;
  stroke: rgb(var(--v-theme-surface));
  stroke-width: 2;
}

.capacity-label {
  fill: rgba(var(--v-theme-on-surface), 0.58);
  font-size: 11px;
}
</style>
